import React, { useState } from 'react';
import { ConfigProvider, theme, Table, Avatar, Checkbox, Space, Button } from 'antd';
import {
    Plus,
    Filter,
    Tag,
    MapPin,
    ArrowRightToLine,
    ArrowLeftToLine,
    Settings,
    ChevronLeft,
    ChevronRight,
    ArrowUpDown,
    LayoutList,
    Phone,
    Moon,
    Sun,
} from 'lucide-react';
import Layout from '../../Layout'
import { Link } from 'react-router-dom';

import { PlusOutlined } from "@ant-design/icons";

import { useNavigate } from 'react-router-dom';

export default function SupplierListCompact() {
    const navigate = useNavigate();
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [activeTab, setActiveTab] = useState('all');

    // Cấu hình Theme Ant Design để bảng nhìn phẳng và khớp với ảnh mẫu
    const antdThemeConfig = {
        token: {
            colorPrimary: '#3e91ab', // Màu chủ đạo bạn yêu cầu
            colorBgContainer: isDarkMode ? '#1f2937' : '#ffffff',
            colorText: isDarkMode ? '#f3f4f6' : '#374151',
            borderRadius: 4,
        },
        components: {
            Table: {
                headerBg: 'transparent',
                headerColor: '#6b7280',
                headerBorderRadius: 0,
                rowHoverBg: isDarkMode ? '#374151' : '#f9fafb',
                paddingContentVertical: 8, // Thu nhỏ padding để bảng gọn như ảnh
            }
        },
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
    };

    // Mock data bám sát hình ảnh mẫu
    const dataSource = [
        { key: '1', assignee: { img: 'https://i.pravatar.cc/150?u=1' }, code: 'NCC_2026_77', name: 'Thu Nhi EatClean', type: 'Cá nhân', phone: null, email: null, date: '25/06/2026' },
        { key: '2', assignee: { text: 'H', color: '#84cc16' }, code: 'NCC_2026_76', name: 'Nha Be Trade', type: 'Cá nhân', phone: null, email: null, date: '23/06/2026' },
        { key: '3', assignee: { text: 'T', color: '#3e91ab' }, code: 'NCC_2026_75', name: 'NGUYỄN NGỌC THÚY', type: 'Cá nhân', phone: '0974020680', email: 'ce8works@gmail.com', date: '19/06/2026' },
        { key: '4', assignee: { text: 'H', color: '#84cc16' }, code: 'NCC_2026_74', name: 'Nguyễn Văn A', type: 'Cá nhân', phone: '0989564123', email: 'nguyenvana@gmail.com', date: '18/06/2026' },
        { key: '5', assignee: { img: 'https://i.pravatar.cc/150?u=2' }, code: 'NCC_2026_73', name: 'CÔNG TY TOP CV', type: 'Cá nhân', phone: '0978345', email: 'trancamvan@gmail.com', date: '23/04/2026' },
        { key: '6', assignee: { img: 'https://i.pravatar.cc/150?u=3' }, code: 'NCC_2026_72', name: 'MOI', type: 'Tổ chức', phone: null, email: null, date: '18/03/2026' },
        { key: '7', assignee: { text: 'T', color: '#3e91ab' }, code: 'NCC_2025_71', name: 'Thầu Phụ An Bình', type: 'Tổ chức', phone: '0979123456', email: '1234@gmail.com', date: '19/12/2025' },
    ];

    // Định nghĩa các cột (Columns)
    const columns = [
        {
            title: 'Phụ trách',
            dataIndex: 'assignee',
            key: 'assignee',
            width: 100,
            render: (assignee) => (
                /* Bọc Link ra ngoài và trỏ đến đường dẫn bạn muốn */
                <Link to="/supplier/detail" className="inline-block transition-opacity hover:opacity-80">
                    {assignee.img ? (
                        <Avatar src={assignee.img} size={28} className="cursor-pointer" />
                    ) : (
                        <Avatar style={{ backgroundColor: assignee.color }} size={28} className="cursor-pointer">
                            {assignee.text}
                        </Avatar>
                    )}
                </Link>
            ),
        },
        { title: 'Mã nhà cung cấp', dataIndex: 'code', key: 'code', className: 'font-medium' },
        { title: 'Tên nhà cung cấp', dataIndex: 'name', key: 'name' },
        { title: 'Loại nhà cung cấp', dataIndex: 'type', key: 'type', responsive: ['md'] },
        {
            title: 'Điện thoại',
            dataIndex: 'phone',
            key: 'phone',
            render: (phone) => phone ? (
                <Space className="text-gray-700 dark:text-gray-300">
                    {/* Đổi icon thành màu #3e91ab theo yêu cầu */}
                    <Phone size={14} className="text-[#3e91ab]" fill="#3e91ab" />
                    {phone}
                </Space>
            ) : <span className="text-gray-400">--</span>,
        },
        { title: 'Email', dataIndex: 'email', key: 'email', render: (email) => email ? email : <span className="text-gray-400">--</span>, responsive: ['lg'] },
        { title: 'Ngày tạo', dataIndex: 'date', key: 'date', align: 'right' },
    ];

    return (
        <Layout>
            <div className="flex gap-2 mb-4 flex justify-between">
                <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                    Danh sách nhà cung cấp
                </h2>
                <div className="items-center gap-4 pb-2 flex sm:hidden">

                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                        onClick={() => navigate(`/supplier/add`)}
                    >
                        Thêm mới
                    </Button>
                </div>
            </div>

            {/* Container chính: Thêm class `supplier-list-page` để bạn dễ overwrite CSS riêng */}
            <div className={`supplier-list-page min-h-screen transition-colors duration-300`}>

                {/* Box chứa bảng (Giống viền xanh nhạt trong ảnh mẫu) */}
                <div className={`w-full overflow-hidden flex flex-col`}>

                    {/* HEADER TABS & NÚT THÊM MỚI */}
                    <div className={`flex justify-between items-end border-b dark:border-[#374151]`}>
                        <div className="overflow-x-auto no-scrollbar">
                            <div className="flex gap-6 text-sm font-medium min-w-[400px]">
                                <div
                                    onClick={() => setActiveTab('all')}
                                    className={`pb-3 cursor-pointer border-b-2 transition-colors ${activeTab === 'all' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Tất cả (51)
                                </div>
                                <div
                                    onClick={() => setActiveTab('mine')}
                                    className={`pb-3 cursor-pointer border-b-2 transition-colors ${activeTab === 'mine' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Bạn phụ trách (0)
                                </div>
                                <div
                                    onClick={() => setActiveTab('department')}
                                    className={`pb-3 cursor-pointer border-b-2 transition-colors ${activeTab === 'department' ? 'border-[#3e91ab] text-[#3e91ab]' : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400'}`}
                                >
                                    Phòng ban bạn (40)
                                </div>
                            </div>
                        </div>

                        <div className="items-center gap-4 pb-2 hidden sm:flex">

                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                className="bg-[#3e91ab] hover:!bg-[#357f96] border-[#3e91ab]"
                                onClick={() => navigate(`/supplier/add`)}
                            >
                                Thêm mới
                            </Button>
                        </div>
                    </div>

                    {/* THANH TOOLBAR & PHÂN TRANG (PAGINATION) */}
                    {/* Bỏ justify-end ở thẻ cha */}
                    <div className={`flex flex-col xl:flex-row items-center py-2 text-xs md:text-sm text-black/80 border-b dark:border-[#374151] overflow-x-auto`}>

                        {/* Thay justify-end bằng ml-auto (đẩy sang phải), và dùng min-w-max để nội dung không bị rớt dòng */}
                        <div className="flex items-center gap-4 xl:gap-5 w-full xl:w-auto ml-auto min-w-max text-000000e0">
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Filter size={14} /> Lọc nhanh</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Tag size={14} /> Nhãn</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><MapPin size={14} /> Cập nhật địa chỉ...</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><ArrowRightToLine size={14} /> Export</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><ArrowLeftToLine size={14} /> Import</span>
                            <span className="flex items-center gap-1.5 cursor-pointer hover:text-[#3e91ab] transition-colors"><Settings size={14} /> Cài đặt</span>
                        </div>

                    </div>

                    {/* BẢNG DỮ LIỆU */}
                    <div className="overflow-x-auto">
                        <Table
                            columns={columns}
                            dataSource={dataSource}
                            rowSelection={{ type: 'checkbox' }}
                            className="custom-ant-table"
                            scroll={{ x: "max-content" }}
                        />
                    </div>

                </div>
            </div>
        </Layout>
    );
}